import { useCallback, useEffect, useRef, useState } from "react";
import { App as AntdApp, Button, Modal } from "antd";
import { useVersionCheck } from "../hooks/useVersionCheck";
import { useUpdateEnforcer } from "../hooks/useUpdateEnforcer";
import {
  COUNTDOWN_SECONDS,
  HARD_ESCALATION_MS,
  RESURFACE_MS,
  shouldHardEscalate,
} from "../utils/updateEscalation";

const NOTIFICATION_KEY = "app-update-available";

const reloadNow = () => window.location.reload();

/**
 * Surfaces the "new version available" prompt when useVersionCheck detects a
 * newer build, and escalates a tab that ignores it:
 *
 *  1. First detection → non-blocking `info` toast (bottom-right), "Reload now".
 *  2. Dismissed → re-surface ~30 min later as a `warning` toast at the top.
 *  3. 3 dismissals OR ~24h stale → a blocking modal with a 60s countdown that
 *     force-reloads (shouldHardEscalate).
 *
 * In parallel, useUpdateEnforcer silently reloads at safe boundaries (tab
 * hidden / ≥15 min idle) — that usually upgrades the tab before the escalation
 * ladder is ever reached; the ladder is the fallback for a continuously-active,
 * repeatedly-dismissing tab.
 *
 * Must render inside antd's <App> so App.useApp() resolves the notification API.
 */
export default function UpdateAvailableNotification() {
  const { updateAvailable, firstDetectedAt } = useVersionCheck();
  const { notification } = AntdApp.useApp();
  useUpdateEnforcer(updateAvailable);

  const shownRef = useRef(false);
  const dismissCountRef = useRef(0);
  const resurfaceTimerRef = useRef(null);
  const [blocking, setBlocking] = useState(false);
  const [countdown, setCountdown] = useState(COUNTDOWN_SECONDS);

  const openBlockingModal = useCallback(() => {
    notification.destroy(NOTIFICATION_KEY);
    if (resurfaceTimerRef.current) clearTimeout(resurfaceTimerRef.current);
    setBlocking(true);
  }, [notification]);

  const showPrompt = useCallback(
    (escalated) => {
      notification.open({
        key: NOTIFICATION_KEY,
        type: escalated ? "warning" : "info",
        message: "A new version is available",
        description: escalated
          ? "You're on an outdated version. Reload to get the latest updates."
          : "Reload to get the latest updates.",
        placement: escalated ? "top" : "bottomRight",
        duration: 0,
        onClose: () => {
          dismissCountRef.current += 1;
          if (shouldHardEscalate(dismissCountRef.current, firstDetectedAt, Date.now())) {
            openBlockingModal();
            return;
          }
          if (resurfaceTimerRef.current) clearTimeout(resurfaceTimerRef.current);
          resurfaceTimerRef.current = setTimeout(
            () => showPrompt(true),
            RESURFACE_MS,
          );
        },
        btn: (
          <Button type="primary" size="small" onClick={reloadNow}>
            Reload now
          </Button>
        ),
      });
    },
    [notification, firstDetectedAt, openBlockingModal],
  );

  // Surface the prompt once, on first detection.
  useEffect(() => {
    if (!updateAvailable || shownRef.current) return;
    shownRef.current = true;
    showPrompt(false);
  }, [updateAvailable, showPrompt]);

  // Hard-escalate to the blocking modal after the build has been stale ~24h,
  // even if the prompt was never dismissed.
  useEffect(() => {
    if (firstDetectedAt == null) return;
    const remaining = HARD_ESCALATION_MS - (Date.now() - firstDetectedAt);
    if (remaining <= 0) {
      openBlockingModal();
      return;
    }
    const t = setTimeout(openBlockingModal, remaining);
    return () => clearTimeout(t);
  }, [firstDetectedAt, openBlockingModal]);

  // Once the blocking modal is open, tick the countdown down to 0 (pure updater).
  useEffect(() => {
    if (!blocking) return;
    setCountdown(COUNTDOWN_SECONDS);
    const iv = setInterval(
      () => setCountdown((s) => Math.max(0, s - 1)),
      1000,
    );
    return () => clearInterval(iv);
  }, [blocking]);

  // Force the reload when the countdown reaches 0 (side effect kept out of the
  // updater so it can't double-fire under StrictMode's double-invoke).
  useEffect(() => {
    if (blocking && countdown === 0) reloadNow();
  }, [blocking, countdown]);

  // Clear a pending re-surface timer on unmount.
  useEffect(
    () => () => {
      if (resurfaceTimerRef.current) clearTimeout(resurfaceTimerRef.current);
    },
    [],
  );

  return (
    <Modal
      open={blocking}
      closable={false}
      maskClosable={false}
      keyboard={false}
      title="Update required"
      footer={[
        <Button key="reload" type="primary" onClick={reloadNow}>
          Reload now
        </Button>,
      ]}
    >
      This tab is running an outdated version and will reload in {countdown}s to
      get the latest updates.
    </Modal>
  );
}
